import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X, Check, PackageSearch } from 'lucide-react';

/**
 * SearchableDropdown - dropdown dengan search/filter bawaan (Premium UI)
 * Props:
 *   options: [{ value, label, sublabel? }]
 *   value: string
 *   onChange: (value) => void
 *   placeholder: string
 *   searchPlaceholder: string
 *   disabled: boolean
 */
export default function SearchableDropdown({ options = [], value, onChange, placeholder = 'Pilih...', searchPlaceholder = 'Cari...', disabled = false, className = '' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = options.find(o => o.value === value);

  const filtered = options.filter(o =>
    o.label?.toLowerCase().includes(search.toLowerCase()) ||
    o.sublabel?.toLowerCase().includes(search.toLowerCase())
  );

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search when opened
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Reset focus on search change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [search]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-dropdown-item]');
      if (items[focusedIndex]) {
        items[focusedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [focusedIndex]);

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch('');
    setFocusedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => prev < filtered.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : filtered.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && filtered[focusedIndex]) {
          handleSelect(filtered[focusedIndex].value);
        }
        break;
      case 'Escape':
        setOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  // Highlight search match in label
  const highlightMatch = (text) => {
    if (!search) return text;
    const idx = text.toLowerCase().indexOf(search.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="bg-[#D4AF37]/25 text-[#6e4c06] font-bold rounded-sm px-0.5">
          {text.slice(idx, idx + search.length)}
        </span>
        {text.slice(idx + search.length)}
      </>
    );
  };

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className={`
          flex h-11 w-full items-center justify-between 
          rounded-xl border-2 bg-white px-3.5 py-2 text-sm 
          transition-all duration-200
          disabled:cursor-not-allowed disabled:opacity-50
          ${open
            ? 'border-[#D4AF37] shadow-[0_0_0_3px_rgba(212,175,55,0.1)]'
            : 'border-[#E6D5B8] hover:border-[#D4AF37]/50 shadow-sm'
          }
        `}
      >
        <span className={selected ? 'text-[#302621] flex items-center gap-1.5 font-medium' : 'text-[#B8A898]'}>
          {selected ? (
            <>
              <span>{selected.label}</span>
              {selected.sublabel && <span className="text-xs text-[#A39171] font-normal">({selected.sublabel})</span>}
            </>
          ) : placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 text-[#A39171] transition-transform duration-300 ${open ? 'rotate-180 text-[#8C6208]' : ''}`} />
      </button>

      {/* Dropdown */}
      <div
        className={`
          absolute z-50 mt-1.5 w-full 
          rounded-xl border border-[#E6D5B8] bg-white 
          shadow-[0_12px_40px_rgba(48,38,33,0.12)]
          overflow-hidden
          transition-all duration-200 ease-out origin-top
          ${open
            ? 'opacity-100 scale-y-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 scale-y-95 -translate-y-1 pointer-events-none'
          }
        `}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[#F0E6D4] bg-[#FDFAF4]">
          <Search className="w-4 h-4 text-[#A39171] shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            className="flex-1 text-sm outline-none bg-transparent placeholder:text-[#C4B29E] text-[#302621] font-medium"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="w-6 h-6 rounded-full flex items-center justify-center text-[#A39171] hover:text-[#302621] hover:bg-[#F4EDE0] transition-all"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Options */}
        <div
          ref={listRef}
          className="max-h-56 overflow-y-auto overscroll-contain"
          style={{ scrollbarWidth: 'thin', scrollbarColor: '#E6D5B8 transparent' }}
        >
          {filtered.length === 0 ? (
            <div className="py-8 text-center">
              <PackageSearch className="w-8 h-8 text-[#E6D5B8] mx-auto mb-2" />
              <p className="text-sm font-semibold text-[#A39171]">Tidak ditemukan</p>
              <p className="text-xs text-[#C4B29E] mt-1">Coba kata kunci lain</p>
            </div>
          ) : (
            filtered.map((o, i) => {
              const isSelected = o.value === value;
              const isFocused = i === focusedIndex;

              return (
                <button
                  key={o.value}
                  type="button"
                  data-dropdown-item
                  onClick={() => handleSelect(o.value)}
                  onMouseEnter={() => setFocusedIndex(i)}
                  className={`
                    w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm text-left
                    transition-all duration-150 ease-out
                    border-b border-[#F9F5EC] last:border-b-0
                    group cursor-pointer
                    ${isFocused
                      ? 'bg-gradient-to-r from-[#F9F5EC] to-[#FFF9ED]'
                      : 'hover:bg-[#FDFAF4]'
                    }
                    ${isSelected ? 'bg-[#F9F5EC]' : ''}
                  `}
                >
                  <span className={`flex-1 truncate transition-colors duration-150 ${
                    isSelected
                      ? 'font-bold text-[#302621]'
                      : isFocused
                      ? 'font-semibold text-[#302621]'
                      : 'font-medium text-[#5C4D3E] group-hover:text-[#302621]'
                  }`}>
                    {highlightMatch(o.label)}
                  </span>
                  {o.sublabel && (
                    <span className="text-xs text-[#A39171] shrink-0 font-normal">
                      {o.sublabel}
                    </span>
                  )}
                  {isSelected && (
                    <div className="w-5 h-5 rounded-full bg-[#D4AF37] flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}